/**
 * @file 校区操作相关弹窗
 * @description 充值 / 扣除、充值记录（真实接口）。
 *
 * @whenToUse 校区管理行操作。
 * @whenNotToUse 创建 / 编辑（用 CampusFormDialog）；重置密码用命令式 confirm。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx'
import {
  fetchCampusRechargeRecords,
  type CampusItem,
  type CampusRechargeRecord,
} from '@/api/campus.ts'

export type CampusPointsDialogMode = 'recharge' | 'deduct'

export type CampusRechargeDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  campus: CampusItem | null
  /** 充值 / 扣除；默认充值 */
  mode?: CampusPointsDialogMode
  onConfirm: (points: number, remark: string) => Promise<void>
}

/**
 * 校区充值 / 扣除点数
 */
export function CampusRechargeDialog({
  open,
  onOpenChange,
  campus,
  mode = 'recharge',
  onConfirm,
}: CampusRechargeDialogProps) {
  const isDeduct = mode === 'deduct'
  const [points, setPoints] = useState(100)
  const [remark, setRemark] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const balance = campus?.points ?? 0

  useEffect(() => {
    if (!open) return
    setPoints(100)
    setRemark('')
    setError('')
  }, [open, mode])

  /**
   * 提交变更点数。
   */
  const handleSubmit = async () => {
    if (!points || points <= 0) {
      setError(`请输入大于 0 的${isDeduct ? '扣除' : '充值'}点数`)
      return
    }
    if (isDeduct && points > balance) {
      setError('扣除点数不能超过当前余额')
      return
    }
    setSubmitting(true)
    try {
      await onConfirm(points, remark.trim())
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isDeduct ? '扣除' : '充值'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button loading={submitting} onClick={handleSubmit}>
            {isDeduct ? '确认扣除' : '确认充值'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          当前「{campus?.campusName}」余额 {balance} 点。
        </p>
        <FormField
          label={isDeduct ? '扣除点数' : '充值点数'}
          required
          error={error}
        >
          <InputNumber
            value={points}
            min={100}
            max={isDeduct ? balance : undefined}
            step={100}
            invalid={Boolean(error)}
            onChange={(value) => setPoints(value ?? 0)}
            onBlur={() => {
              const snapped = Math.round(points / 100) * 100
              const next = Math.max(snapped, 100)
              setPoints(
                isDeduct && balance > 0 ? Math.min(next, balance) : next,
              )
            }}
          />
        </FormField>
        <FormField label="备注">
          <Textarea
            value={remark}
            placeholder="请输入备注(最多50字)"
            rows={3}
            maxLength={50}
            onChange={(event) => setRemark(event.target.value)}
          />
        </FormField>
      </div>
    </Dialog>
  )
}

export type CampusRechargeRecordsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  campus: CampusItem | null
}

/**
 * 充值记录列表（打开时按校区拉取，一次展示最近 50 条）
 */
export function CampusRechargeRecordsDialog({
  open,
  onOpenChange,
  campus,
}: CampusRechargeRecordsDialogProps) {
  const [records, setRecords] = useState<CampusRechargeRecord[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !campus) return
    setLoading(true)
    fetchCampusRechargeRecords({
      campusId: campus.id,
      pageNum: 1,
      pageSize: 50,
    })
      .then(({ rows }) => setRecords(rows))
      .finally(() => setLoading(false))
  }, [open, campus])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${campus?.campusName ?? ''} · 充值记录`}
      className="max-w-2xl"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          关闭
        </Button>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>时间</TableHead>
            <TableHead>点数</TableHead>
            <TableHead>备注</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableEmpty colSpan={3} title="加载中…" />
          ) : records.length === 0 ? (
            <TableEmpty colSpan={3} title="暂无充值记录" />
          ) : (
            records.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.createTime}</TableCell>
                <TableCell>
                  {record.points > 0 ? `+${record.points}` : record.points}
                </TableCell>
                <TableCell>{record.remark || '—'}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Dialog>
  )
}
